/**
 * @fileoverview Presentational table for staging external URLs (URL-to-Variant/Tag mapping).
 * Replaces the legacy GoogleLinksTable with the generalized URL matching pattern.
 * @module features/sku-alias/components/ExternalUrlsTable
 */

import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Loader2, ExternalLink, Undo2, Sparkles } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { TagChip, type Tag } from '@/features/tags'
import type { Variant } from '@/lib/variantService'
import type { StagingExternalUrl } from '@/features/sku-alias/api/stagingExternalUrls'
import { UrlAssignmentCell } from './UrlAssignmentCell'

/**
 * Props for ExternalUrlsTable.
 */
export interface ExternalUrlsTableProps {
  /** Array of URLs to render */
  urls: StagingExternalUrl[]
  /** Tags available for assignment (full list, surfaced first on /collections/* paths) */
  tags: Tag[]
  /** Handler for variant selection on a URL */
  onVariantSelect: (url: StagingExternalUrl, variant: Variant) => Promise<void> | void
  /** Handler for tag selection on a URL */
  onTagSelect: (url: StagingExternalUrl, tagUuid: string) => Promise<void> | void
  /** Handler for assigning URL to site-wide (general) variant */
  onAssignToGeneral?: ((url: StagingExternalUrl) => Promise<void> | void) | undefined
  /** Handler for unassigning URL from variant */
  onUnassign?: ((url: StagingExternalUrl) => Promise<void> | void) | undefined
  /** Handler for unassigning URL from tag */
  onUnassignTag?: ((url: StagingExternalUrl) => Promise<void> | void) | undefined
  /** Handler for clearing/rejecting AI suggestion */
  onClearSuggestion?: ((url: StagingExternalUrl) => Promise<void> | void) | undefined
  /** Loading state for assignments */
  isLoading?: boolean | undefined
  /** ID of the general variant (for disable state) */
  generalVariantId?: number | undefined
  /** Whether showing assigned (resolved) URLs */
  showAssigned?: boolean | undefined
  /** Handler for generating AI suggestions */
  onGenerateAiSuggestions?: (() => void) | undefined
  /** Loading state for AI suggestion generation */
  isGeneratingAiSuggestions?: boolean | undefined
}

/**
 * Presentational table for URL-to-Variant/Tag mapping with inline unified selection.
 *
 * @component
 * @example
 * ```tsx
 * <ExternalUrlsTable urls={urls} tags={tags} onVariantSelect={...} onTagSelect={...} />
 * ```
 */
export function ExternalUrlsTable({
  urls,
  tags,
  onVariantSelect,
  onTagSelect,
  onAssignToGeneral,
  onUnassign,
  onUnassignTag,
  onClearSuggestion,
  isLoading = false,
  showAssigned = false,
  onGenerateAiSuggestions,
  isGeneratingAiSuggestions = false,
}: ExternalUrlsTableProps): ReactElement {
  const { t } = useTranslation('features-google-links')

  const confirmIcon = useMemo(
    () => (isLoading ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />),
    [isLoading]
  )

  const handleUnassign = useCallback(
    async (url: StagingExternalUrl): Promise<void> => {
      await onUnassign?.(url)
    },
    [onUnassign]
  )

  const handleUnassignTag = useCallback(
    async (url: StagingExternalUrl): Promise<void> => {
      await onUnassignTag?.(url)
    },
    [onUnassignTag]
  )

  /**
   * Format source for display.
   */
  const formatSource = useCallback((source: string): string => {
    const sourceMap: Record<string, string> = {
      google_ads: 'Google Ads',
      tiktok_ads: 'TikTok Ads',
      microsoft_ads: 'Microsoft Ads',
      facebook_ads: 'Meta Ads',
    }
    return sourceMap[source] ?? source
  }, [])

  return (
    <div className="max-w-full overflow-x-auto flex-1">
      <Table className="table-fixed w-full">
        <TableHeader>
          <TableRow className="[&_th]:h-14">
            <TableHead className="whitespace-nowrap text-xs font-semibold tracking-wide text-muted-foreground pl-6">
              {t('table.columns.url')}
            </TableHead>
            <TableHead className="whitespace-nowrap text-xs font-semibold tracking-wide text-muted-foreground text-right w-96">
              {!showAssigned && onGenerateAiSuggestions ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onGenerateAiSuggestions}
                  disabled={isGeneratingAiSuggestions}
                  className="h-8 px-3 text-xs font-medium"
                >
                  {isGeneratingAiSuggestions ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin" />
                      {t('table.ai.generating')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="size-3.5" />
                      {t('table.ai.generateSuggestions')}
                    </>
                  )}
                </Button>
              ) : (
                t('table.columns.variant')
              )}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {urls.map(url => (
            <TableRow key={url.uuid}>
              <TableCell className="py-4 text-muted-foreground text-sm max-w-0 pl-6">
                <div className="space-y-1.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-foreground">
                          {url.canonicalPath}
                        </span>
                        <Badge
                          variant="secondary"
                          className="inline-flex w-fit items-center gap-1 rounded-full bg-muted/50 text-[11px] text-muted-foreground px-2 py-1 capitalize hover:bg-muted/70 shrink-0"
                        >
                          {formatSource(url.source)}
                        </Badge>
                        <a
                          href={url.rawUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground/40 hover:text-muted-foreground shrink-0"
                          onClick={e => e.stopPropagation()}
                        >
                          <ExternalLink className="size-3.5" />
                        </a>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" align="start">
                      <p className="max-w-md break-all text-xs">{url.rawUrl}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TableCell>
              <TableCell className="py-4 text-muted-foreground text-base w-96 text-right">
                {showAssigned ? (
                  <div className="flex items-center justify-end gap-2">
                    <div className="flex-1 flex justify-end min-w-0">
                      {url.tagUuid && url.tagName ? (
                        <TagChip tag={{ name: url.tagName }} />
                      ) : (
                        <span className="text-sm font-medium text-foreground truncate block">
                          {url.variantSku ?? t('table.variants.unknown')}
                        </span>
                      )}
                    </div>
                    {url.tagUuid && url.tagName
                      ? onUnassignTag && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnassignTag(url)}
                                disabled={isLoading}
                                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                              >
                                <Undo2 className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>{t('table.actions.unassign')}</p>
                            </TooltipContent>
                          </Tooltip>
                        )
                      : onUnassign && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleUnassign(url)}
                                disabled={isLoading}
                                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                              >
                                <Undo2 className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>{t('table.actions.unassign')}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <UrlAssignmentCell
                      url={url}
                      tags={tags}
                      onVariantSelect={onVariantSelect}
                      onTagSelect={onTagSelect}
                      onAssignToGeneral={onAssignToGeneral ? onAssignToGeneral : undefined}
                      onClearSuggestion={onClearSuggestion ? onClearSuggestion : undefined}
                      isLoading={isLoading}
                      hasSuggestion={url.suggestedVariantId !== null}
                      variantLabel={t('table.columns.variant')}
                      siteWideLabel={t('table.variants.siteWide')}
                      aiSuggestedMatchLabel={t('table.ai.suggestedMatch')}
                      confirmAriaLabel={t('table.actions.confirmVariant')}
                      rejectAriaLabel={t('table.actions.rejectSuggestion')}
                      confirmIcon={confirmIcon}
                    />
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export default ExternalUrlsTable
